/**
 * Chronos brand mark.
 *
 * Prefer this over hardcoding asset paths so alt text, sizing, and variant
 * choice stay consistent wherever branding appears.
 */

import { cn } from '@/lib/cn';

type Props = {
  /** `wordmark` = full logo plate; `mark` = clock favicon for tight chrome. */
  variant?: 'wordmark' | 'mark';
  /** Visual size. `xs` fits the app chrome without crowding nav. */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /** Hide the visible title when a nearby heading already names the app. */
  decorative?: boolean;
};

const WORDMARK_HEIGHT = {
  xs: 'h-5',
  sm: 'h-8',
  md: 'h-10',
  lg: 'h-14',
} as const;

const MARK_SIZE = {
  xs: 'size-5',
  sm: 'size-7',
  md: 'size-9',
  lg: 'size-11',
} as const;

export function BrandLogo({
  variant = 'wordmark',
  size = 'md',
  className,
  decorative = false,
}: Props) {
  if (variant === 'mark') {
    return (
      <img
        src="/favicon.svg"
        alt={decorative ? '' : 'Chronos'}
        aria-hidden={decorative || undefined}
        className={cn('block shrink-0 select-none rounded-md', MARK_SIZE[size], className)}
        decoding="async"
      />
    );
  }

  return (
    <img
      src="/logo.png"
      alt={decorative ? '' : 'Chronos'}
      aria-hidden={decorative || undefined}
      className={cn(
        'block w-auto max-w-full select-none',
        WORDMARK_HEIGHT[size],
        // Wordmark ships on black; round the plate so it sits cleanly on light UI.
        'rounded-md',
        className,
      )}
      decoding="async"
    />
  );
}
